import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';

export interface ToastConfig {
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
  showIcon?: boolean;
  showCloseButton?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private renderer: Renderer2;
  private toastContainer: HTMLElement | null = null;
  private toastQueue: ToastConfig[] = [];
  private isProcessing = false;

  constructor(rendererFactory: RendererFactory2) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  /**
   * Show a success toast notification
   */
  success(message: string, config?: Partial<ToastConfig>): void {
    this.show({
      message,
      type: 'success',
      ...config
    });
  }

  /**
   * Show an error toast notification
   */
  error(message: string, config?: Partial<ToastConfig>): void {
    this.show({
      message,
      type: 'error',
      ...config
    });
  }

  /**
   * Show a warning toast notification
   */
  warning(message: string, config?: Partial<ToastConfig>): void {
    this.show({
      message,
      type: 'warning',
      ...config
    });
  }

  /**
   * Show an info toast notification
   */
  info(message: string, config?: Partial<ToastConfig>): void {
    this.show({
      message,
      type: 'info',
      ...config
    });
  }

  /**
   * Show a toast notification with custom configuration
   */
  show(config: ToastConfig): void {
    const defaultConfig: ToastConfig = {
      message: '',
      type: 'info',
      duration: 5000,
      position: 'top-right',
      showIcon: true,
      showCloseButton: true
    };

    const finalConfig = { ...defaultConfig, ...config };
    this.toastQueue.push(finalConfig);

    if (!this.isProcessing) {
      this.processQueue();
    }
  }

  /**
   * Clear all toast notifications
   */
  clearAll(): void {
    if (this.toastContainer) {
      this.renderer.removeChild(document.body, this.toastContainer);
      this.toastContainer = null;
    }
    this.toastQueue = [];
    this.isProcessing = false;
  }

  /**
   * Process the toast queue
   */
  private processQueue(): void {
    if (this.toastQueue.length === 0) {
      this.isProcessing = false;
      return;
    }

    this.isProcessing = true;
    const config = this.toastQueue.shift()!;
    this.createToast(config);
  }

  /**
   * Create and display a toast notification
   */
  private createToast(config: ToastConfig): void {
    if (!this.toastContainer) {
      this.createToastContainer(config.position!);
    }

    const toast = this.renderer.createElement('div');
    const toastClass = `toast toast-${config.type}`;
    this.renderer.setAttribute(toast, 'class', toastClass);

    // Create toast content
    const content = this.createToastContent(config);
    this.renderer.appendChild(toast, content);

    // Add toast to container
    this.renderer.appendChild(this.toastContainer, toast);

    // Add entrance animation
    setTimeout(() => {
      this.renderer.addClass(toast, 'show');
    }, 100);

    // Auto remove after duration
    if (config.duration && config.duration > 0) {
      setTimeout(() => {
        this.removeToast(toast);
      }, config.duration);
    }

    // Process next toast
    setTimeout(() => {
      this.processQueue();
    }, 100);
  }

  /**
   * Create toast container
   */
  private createToastContainer(position: string): void {
    this.toastContainer = this.renderer.createElement('div');
    this.renderer.setAttribute(this.toastContainer, 'id', 'toast-container');
    this.renderer.setAttribute(this.toastContainer, 'class', `toast-container toast-${position.replace('-', '-')}`);
    this.renderer.appendChild(document.body, this.toastContainer);
  }

  /**
   * Create toast content
   */
  private createToastContent(config: ToastConfig): HTMLElement {
    const content = this.renderer.createElement('div');
    this.renderer.setAttribute(content, 'class', 'toast-content');

    // Icon
    if (config.showIcon) {
      const icon = this.renderer.createElement('i');
      const iconClass = this.getIconClass(config.type);
      this.renderer.setAttribute(icon, 'class', iconClass);
      this.renderer.appendChild(content, icon);
    }

    // Message
    const message = this.renderer.createElement('span');
    this.renderer.setAttribute(message, 'class', 'toast-message');
    message.textContent = config.message;
    this.renderer.appendChild(content, message);

    // Close button
    if (config.showCloseButton) {
      const closeBtn = this.renderer.createElement('button');
      this.renderer.setAttribute(closeBtn, 'class', 'toast-close');
      this.renderer.setAttribute(closeBtn, 'type', 'button');
      this.renderer.setAttribute(closeBtn, 'aria-label', 'Close');
      
      const closeIcon = this.renderer.createElement('i');
      this.renderer.setAttribute(closeIcon, 'class', 'fas fa-times');
      this.renderer.appendChild(closeBtn, closeIcon);
      
      this.renderer.listen(closeBtn, 'click', () => {
        this.removeToast(content.parentElement!);
      });
      
      this.renderer.appendChild(content, closeBtn);
    }

    return content;
  }

  /**
   * Get icon class based on toast type
   */
  private getIconClass(type: string): string {
    const iconMap: { [key: string]: string } = {
      success: 'fas fa-check-circle',
      error: 'fas fa-exclamation-circle',
      warning: 'fas fa-exclamation-triangle',
      info: 'fas fa-info-circle'
    };
    return iconMap[type] || iconMap['info'];
  }

  /**
   * Remove a specific toast
   */
  private removeToast(toast: HTMLElement): void {
    this.renderer.removeClass(toast, 'show');
    this.renderer.addClass(toast, 'hide');
    
    setTimeout(() => {
      if (toast.parentElement) {
        this.renderer.removeChild(toast.parentElement, toast);
      }
      
      // Remove container if no more toasts
      if (this.toastContainer && this.toastContainer.children.length === 0) {
        this.renderer.removeChild(document.body, this.toastContainer);
        this.toastContainer = null;
      }
    }, 300);
  }
}
